import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useSubmitUsRegistration, type TrustCenterState, type UsRegistrationForm } from '@/api/trustCenter'

const VIDE: UsRegistrationForm = {
  business_name: '', business_type: 'corporation', business_number: '', industry: 'PROFESSIONAL_SERVICES', website: '', business_email: '',
  address1: '', city: '', province: '', postal: '', country: 'CA',
  rep_first_name: '', rep_last_name: '', rep_email: '', rep_phone: '', rep_title: 'ceo',
  use_case: 'service_client', description: '', sample1: '', sample2: '', consent: '', has_links: false, has_phone: false, numbers: [],
}

/**
 * Dossier « textos vers les États-Unis » : un seul formulaire pour le client, en trois pas
 * (entreprise, représentant, textos), puis le paiement des frais des opérateurs.
 */
export function UsRegistrationDialog({ open, onClose, companyId, etat }: { open: boolean; onClose: () => void; companyId: number | undefined; etat: TrustCenterState }) {
  const { t } = useTranslation()
  const [pas, setPas] = useState(0)
  const [f, setF] = useState<UsRegistrationForm>(VIDE)
  const soumettre = useSubmitUsRegistration(companyId)
  const dateLocale = 'fr-CA'
  const argent = (cents: number) => new Intl.NumberFormat(dateLocale, { style: 'currency', currency: 'CAD' }).format(cents / 100)

  useEffect(() => {
    if (open) {
      const ancien = (etat.us.details ?? {}) as Partial<UsRegistrationForm>
      setF({ ...VIDE, ...etat.prefill, ...ancien, numbers: etat.local_numbers.map((n) => n.id) })
      setPas(0)
    }
  }, [open, etat])

  const set = (k: keyof UsRegistrationForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((x) => ({ ...x, [k]: e.target.value }))
  const setV = (k: keyof UsRegistrationForm, v: string | boolean | number[]) => setF((x) => ({ ...x, [k]: v }))
  const rempli = (ks: (keyof UsRegistrationForm)[]) => ks.every((k) => String(f[k] ?? '').trim())
  const pas1 = rempli(['business_name', 'business_type', 'business_number', 'industry', 'website', 'address1', 'city', 'province', 'postal'])
  const pas2 = rempli(['rep_first_name', 'rep_last_name', 'rep_email', 'rep_phone', 'rep_title'])
  const pas3 = rempli(['use_case', 'description', 'sample1', 'sample2', 'consent']) && f.numbers.length > 0
  const courrielGenerique = /@(gmail|yahoo|hotmail|outlook|live|icloud)\./i.test(f.business_email || '')

  const envoyer = async () => {
    try {
      await soumettre.mutateAsync(f)
      toast.success(t('manager.trust.form.submitted'))
      onClose()
    } catch (e) {
      const d = isAxiosError(e) ? (e.response?.data as { error?: string; detail?: string } | undefined) : undefined
      const code = d?.error || 'submission_failed'
      toast.error(t(`manager.trust.form.errors.${code}`, t('manager.trust.form.errors.submission_failed')), { description: d?.detail })
    }
  }

  const champ = (k: keyof UsRegistrationForm, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}, aide?: string) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</Label>
      <Input value={String(f[k] ?? '')} onChange={set(k)} {...props} />
      {aide && <p className="text-[11px] text-slate-400">{aide}</p>}
    </div>
  )
  const zone = (k: keyof UsRegistrationForm, label: string, placeholder: string, rows = 2) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</Label>
      <textarea rows={rows} value={String(f[k] ?? '')} onChange={set(k)} placeholder={placeholder}
        className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
    </div>
  )
  const choix = (k: keyof UsRegistrationForm, label: string, options: string[], cle: string) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</Label>
      <Select value={String(f[k])} onValueChange={(v) => setV(k, v)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((o) => <SelectItem key={o} value={o}>{t(`manager.trust.options.${cle}.${o}`, o)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )

  const etapes = [t('manager.trust.form.stepBusiness'), t('manager.trust.form.stepRep'), t('manager.trust.form.stepTexts')]

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent showCloseButton={false} className="sm:max-w-2xl overflow-hidden border-0 p-0 shadow-2xl">
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600" />
        <div className="px-6 pt-6">
          <DialogTitle className="text-xl tracking-tight">{t('manager.trust.us.register')}</DialogTitle>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('manager.trust.form.subtitle')}</p>
          <ol className="mt-4 flex items-center gap-2 text-xs">
            {etapes.map((e, i) => (
              <li key={e} className={cn('flex items-center gap-1.5', i === pas ? 'text-blue-700 dark:text-blue-300 font-semibold' : 'text-slate-400')}>
                <span className={cn('inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px]', i === pas ? 'bg-blue-600 text-white' : i < pas ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 dark:bg-slate-800')}>{i + 1}</span>
                {e}
                {i < etapes.length - 1 && <span className="mx-1 h-px w-6 bg-slate-200 dark:bg-slate-700" />}
              </li>
            ))}
          </ol>
        </div>

        <div className="px-6 pt-5 pb-2 space-y-4 max-h-[60vh] overflow-y-auto">
          {pas === 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {champ('business_name', t('manager.trust.form.businessName'))}
              {choix('business_type', t('manager.trust.form.businessType'), etat.options.business_types, 'businessTypes')}
              {champ('business_number', t('manager.trust.form.businessNumber'), { placeholder: '123456789RC0001' }, t('manager.trust.form.businessNumberHelp'))}
              {choix('industry', t('manager.trust.form.industry'), etat.options.industries, 'industries')}
              {champ('website', t('manager.trust.form.website'), { placeholder: 'https://…' })}
              {champ('business_email', t('manager.trust.form.businessEmail'), { type: 'email', placeholder: 'info@votre-entreprise.ca' },
                courrielGenerique ? t('manager.trust.form.businessEmailWarn') : t('manager.trust.form.businessEmailHelp'))}
              <div className="sm:col-span-2">{champ('address1', t('manager.trust.form.address'))}</div>
              {champ('city', t('manager.trust.form.city'))}
              <div className="grid grid-cols-2 gap-3">
                {champ('province', t('manager.trust.form.province'), { placeholder: 'QC' })}
                {champ('postal', t('manager.trust.form.postal'), { placeholder: 'H2X 1Y4' })}
              </div>
            </div>
          )}

          {pas === 1 && (
            <div className="grid sm:grid-cols-2 gap-3">
              <p className="sm:col-span-2 text-sm text-slate-500 dark:text-slate-400">{t('manager.trust.form.repIntro')}</p>
              {champ('rep_first_name', t('manager.trust.form.firstName'))}
              {champ('rep_last_name', t('manager.trust.form.lastName'))}
              {champ('rep_email', t('manager.trust.form.email'), { type: 'email' })}
              {champ('rep_phone', t('manager.trust.form.phone'), { type: 'tel' })}
              {choix('rep_title', t('manager.trust.form.title'), etat.options.titles, 'titles')}
            </div>
          )}

          {pas === 2 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('manager.trust.form.numbers')}</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {etat.local_numbers.map((n) => {
                    const actif = f.numbers.includes(n.id)
                    return (
                      <button key={n.id} type="button" onClick={() => setV('numbers', actif ? f.numbers.filter((x) => x !== n.id) : [...f.numbers, n.id])}
                        className={cn('rounded-full border px-3 py-1 text-xs', actif ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300')}>
                        {n.label ? `${n.label} · ` : ''}{n.phone_number}
                      </button>
                    )
                  })}
                </div>
              </div>
              {choix('use_case', t('manager.trust.form.useCase'), etat.options.use_cases, 'useCases')}
              {zone('description', t('manager.trust.form.description'), t('manager.trust.form.descriptionPlaceholder'))}
              {zone('sample1', t('manager.trust.form.sample1'), t('manager.trust.form.sample1Placeholder'))}
              {zone('sample2', t('manager.trust.form.sample2'), t('manager.trust.form.sample2Placeholder'))}
              {zone('consent', t('manager.trust.form.consent'), t('manager.trust.form.consentPlaceholder'), 3)}
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.trust.form.consentHelp')}</p>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                {etat.mock
                  ? t('manager.trust.form.mockNote')
                  : etat.us.paid
                    ? t('manager.trust.form.feesPaidNote')
                    : t('manager.trust.form.feesNote', { setup: argent(etat.us.setup_fee_cents), monthly: argent(etat.us.monthly_fee_cents || 300) })}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 border-t bg-slate-50 dark:bg-slate-800/50 px-6 py-4">
          <Button type="button" variant="ghost" onClick={pas === 0 ? onClose : () => setPas(pas - 1)} disabled={soumettre.isPending}>
            {pas === 0 ? t('manager.calendars.modal.cancel') : t('manager.calendars.modal.back')}
          </Button>
          {pas < 2 ? (
            <Button type="button" onClick={() => setPas(pas + 1)} disabled={pas === 0 ? !pas1 : !pas2} className="bg-blue-600 hover:bg-blue-700 text-white">
              {t('manager.calendars.modal.continue')}
            </Button>
          ) : (
            <Button type="button" onClick={() => void envoyer()} disabled={!pas3 || soumettre.isPending} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              {soumettre.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {etat.mock || etat.us.paid ? t('manager.trust.form.submit') : t('manager.trust.form.submitAndPay', { setup: argent(etat.us.setup_fee_cents) })}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
